import { FunctionComponent, useEffect, useState } from 'react'
import { Location, Navigate, useLocation } from 'react-router-dom'
import styles from './draftingPage.module.scss'
import { useAuth } from '@/modules/context/AuthContext'
import { useLazyQuery } from '@apollo/client'
import Loading from '@/components/Loading/Loading'
import DraftingHandler from './list/draftingHandler'
import { SectionGroup } from '@/types/Section'
import { GET_SECTION_GROUPS } from '@/modules/API/staff/staffRate'
import { OccasionalSlotsPlaces } from '@/types/Slots'
import { GET_PLACES } from '@/modules/API/daycare/daycareHandler'
import { GET_CHILD_LIST } from '@/modules/API/parent/parentHandler'
import { StaffWithLinks } from '@/types/Staff'
import { GET_STAFF_LIST } from '@/modules/API/staff/staffHandler'
import { ChildWithParents } from '@/types/Child'

const DraftingPage: FunctionComponent = () => {
    const { user } = useAuth()

    const [places, setPlaces] = useState<OccasionalSlotsPlaces[]>([])

    const [getDaycarePlaces, { data: dataDaycarePlaces }] = useLazyQuery(GET_PLACES, {
        variables: {
            daycareId: user?.currentDaycare?.id
        }
    })

    useEffect(() => {
        if (dataDaycarePlaces) {
            setPlaces(dataDaycarePlaces.getOccasionalSlotsPlaces)
        }
    }, [dataDaycarePlaces])

    const [childs, setChilds] = useState<ChildWithParents[]>([])

    const [getChildList, { data: dataChildList }] = useLazyQuery(GET_CHILD_LIST, {
        variables: {
            daycareId: user?.currentDaycare?.id
        }
    })

    useEffect(() => {
        if (dataChildList) {
            setChilds(dataChildList.getChildList)
        }
    }, [dataChildList])

    const [staffsListData, setStaffsListData] = useState<Array<StaffWithLinks>>([])

    const [getStaffList, { data: dataStaffList }] = useLazyQuery(GET_STAFF_LIST, {
        variables: {
            daycareId: user?.currentDaycare?.id
        }
    })

    useEffect(() => {
        if (dataStaffList) {
            setStaffsListData(dataStaffList.getStaffList)
        }
    }, [dataStaffList])

    const [staffRateList, setStaffRateList] = useState<Array<SectionGroup>>([])

    const [getStaffRate, { loading: loadingSectionGroups, data: dataSectionGroups }] = useLazyQuery<{
        getSectionGroups: Array<SectionGroup>
    }>(GET_SECTION_GROUPS, {
        fetchPolicy: 'network-only',
        variables: {
            daycareId: Number(user?.currentDaycare?.id)
        }
    })

    useEffect(() => {
        if (dataSectionGroups) {
            setStaffRateList(
                [...dataSectionGroups.getSectionGroups].sort((a: SectionGroup, b: SectionGroup) =>
                    a.name.localeCompare(b.name)
                )
            )
        }
    }, [dataSectionGroups])

    useEffect(() => {
        if (user?.currentDaycare) {
            setStaffRateList([])
            getStaffRate()
            setPlaces([])
            getDaycarePlaces()
            setChilds([])
            getChildList()
            setStaffsListData([])
            getStaffList()
        }
    }, [user?.currentDaycare])

    const urlDaycare: Location = useLocation()
    const urlSearchParams: URLSearchParams = new URLSearchParams(urlDaycare.search)

    return user && user.currentDaycare && user.currentDaycareRights.hasStaffAccess ? (
        <div className={styles.PagePosition}>
            {loadingSectionGroups && <Loading overall />}
            <DraftingHandler
                staffRateList={staffRateList}
                currentDaycare={user.currentDaycare}
                places={places}
                childs={childs}
                staffsListData={staffsListData}
                defaultDate={urlSearchParams.get('date')}
                defaultSectionGroupId={urlSearchParams.get('id')}
            />
        </div>
    ) : (
        <Navigate to='/' replace />
    )
}

export default DraftingPage
